import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Cancel } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../utils/FirebaseConfig";

const PAYMENT_STATUS_API = "https://us-central1-etikaf-admin.cloudfunctions.net/getEtikafPaymentStatus";

export default function Result() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const type = searchParams.get("type"); // package / admin / etv / renewal
    const queryStatus = searchParams.get("status"); // paid / failed
    const submitterId = searchParams.get("submitterId"); // 🔑 kita guna ni untuk semak Firestore
    const [status, setStatus] = useState(queryStatus || "checking");
    const [paymentData, setPaymentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState("");

    // ✅ 1. Ambil maklumat semasa createBill dari localStorage
    useEffect(() => {
        const storedPayment = localStorage.getItem("lastPaymentData");
        if (storedPayment) {
            setPaymentData(JSON.parse(storedPayment));
        }
    }, []);

    // Semak melalui endpoint pemilik akaun; tiada akses terus kepada koleksi payments.
    useEffect(() => {
        if (!submitterId) {
            setStatus("not_found");
            setLoading(false);
            return;
        }

        let stopped = false;
        let pollTimer;
        let attempts = 0;

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (!user) {
                setStatus("auth_required");
                setStatusMessage("Sesi pendaftaran tidak ditemui. Log masuk menggunakan e-mel pendaftaran anda.");
                setLoading(false);
                return;
            }

            const poll = async () => {
                if (stopped) return;
                attempts += 1;

                try {
                    const idToken = await user.getIdToken();
                    const response = await fetch(PAYMENT_STATUS_API, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${idToken}`,
                        },
                        body: JSON.stringify({ submitterId }),
                    });
                    const data = await response.json();
                    if (!response.ok) throw Object.assign(new Error(data?.error || "Status bayaran tidak dapat disemak."), { status: response.status });

                    setPaymentData((previous) => ({ ...previous, ...data }));
                    setStatus(data.status || "pending");
                    setStatusMessage("");
                    setLoading(false);

                    if (["paid", "failed", "deleted", "expired"].includes(data.status)) return;
                } catch (error) {
                    console.error("Ralat semak status bayaran:", error);
                    if ([401, 403, 404].includes(error?.status)) {
                        setStatus("error");
                        setStatusMessage(error.message);
                        setLoading(false);
                        return;
                    }
                }

                if (attempts >= 45) {
                    setStatus("timeout");
                    setStatusMessage("Pengesahan mengambil masa lebih lama daripada biasa. Bayaran anda tidak hilang; sila muat semula halaman ini.");
                    setLoading(false);
                    return;
                }
                pollTimer = window.setTimeout(poll, 2000);
            };

            poll();
        });

        return () => {
            stopped = true;
            window.clearTimeout(pollTimer);
            unsubscribe();
        };
    }, [submitterId]);

    // Selepas callback selesai mencipta akaun, muat semula aplikasi supaya profil baharu dibaca.
    useEffect(() => {
        if (status !== "paid" || !paymentData?.activated || !["package", "admin"].includes(type)) return undefined;
        const redirectTimer = window.setTimeout(() => {
            window.location.replace("/");
        }, 2500);
        return () => window.clearTimeout(redirectTimer);
    }, [paymentData?.activated, status, type]);

    // 💅 UI styling
    const cardStyle = {
        textAlign: "center",
        maxWidth: 480,
        margin: "80px auto",
        padding: "40px 30px",
        borderRadius: "16px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
        background: "#fff",
    };

    const iconStyle = {
        fontSize: 96,
        color: status === "paid" ? "#28a745" : "#d93025",
        marginBottom: 20,
    };

    const buttonStyle = {
        padding: "12px 24px",
        borderRadius: "8px",
        fontSize: 16,
        fontWeight: 600,
        border: "none",
        cursor: "pointer",
        marginTop: 25,
    };

    const btnPrimary = {
        ...buttonStyle,
        backgroundColor: "#7A1777",
        color: "white",
    };

    const btnOutline = {
        ...buttonStyle,
        backgroundColor: "white",
        color: "#7A1777",
        border: "2px solid #7A1777",
    };

    // ⏳ Loading spinner
    if (loading) {
        return (
            <div style={{ textAlign: "center", marginTop: "120px" }}>
                <CircularProgress />
                <p style={{ marginTop: 12 }}>Memeriksa status pembayaran...</p>
            </div>
        );
    }

    // ⚙️ Tentukan mesej berdasarkan status akhir
    const isPaid = status === "paid";
    const isPending = ["checking", "pending", "processing", "preparing_payment", "due"].includes(status);

    return (
        <div style={cardStyle}>
            {/* ✅ / ❌ Ikon */}
            {isPaid ? <CheckCircle style={iconStyle} /> : isPending ? <CircularProgress size={82} style={{ marginBottom: 24 }} /> : <Cancel style={iconStyle} />}

            <h2 style={{ marginBottom: 10 }}>
                {isPaid ? "Pembayaran Berjaya" : isPending ? "Mengesahkan bayaran" : "Bayaran belum dapat disahkan"}
            </h2>

            <p style={{ color: "#666", lineHeight: 1.6, marginBottom: 25 }}>
                {isPaid
                    ? type === "package"
                        ? "Pakej lengkap Etikaf telah berjaya didaftarkan. Akaun Sistem Admin aktif dan pesanan peranti ETV sedang diproses untuk penghantaran."
                        : type === "admin"
                        ? "Langganan tahunan Sistem Etikaf telah berjaya. Akaun anda kini aktif."
                        : type === "renewal"
                            ? "Pembayaran berjaya. Langganan Sistem Etikaf anda telah diperbaharui untuk tempoh 1 tahun."
                            : "Pembayaran ETV berjaya. Peranti ETV anda kini diaktifkan."
                    : isPending
                        ? "Kami sedang menunggu pengesahan terus daripada Billplz. Halaman ini akan dikemas kini secara automatik."
                        : statusMessage || "Transaksi tidak berjaya atau belum disahkan. Sila cuba semula kemudian."}
            </p>

            {/* Maklumat transaksi */}
            {paymentData && (
                <div
                    style={{
                        background: "#fafafa",
                        borderRadius: "8px",
                        padding: "15px 20px",
                        textAlign: "left",
                        fontSize: "14px",
                        marginBottom: 25,
                        lineHeight: 1.6,
                    }}
                >
                    <div><strong>Nama:</strong> {paymentData.name}</div>
                    <div><strong>Emel:</strong> {paymentData.email}</div>
                    <div><strong>Jumlah:</strong> RM{Number(paymentData.amount || 0).toLocaleString("ms-MY")}</div>
                    <div><strong>Status:</strong> {status}</div>
                    <div><strong>Tarikh:</strong> {new Date().toLocaleString("ms-MY")}</div>
                </div>
            )}

            {/* Butang tindakan */}
            {isPaid && (type === "package" || type === "admin") ? (
                <button
                    style={btnPrimary}
                    onClick={() => { window.location.href = "/"; }}
                >
                    Masuk Sistem Admin
                </button>
            ) : isPaid && type === "renewal" ? (
                <button
                    style={btnPrimary}
                    onClick={() => {
                        localStorage.removeItem("userProfile");
                        localStorage.removeItem("subscriptionEnd");
                        localStorage.removeItem("mosqueId");
                        localStorage.removeItem("etvUser");
                        window.location.href = "/";
                    }}
                >
                    Masuk Semula ke Sistem
                </button>
            ) : isPaid && type === "etv" ? (
                <button
                    style={isPaid ? btnPrimary : btnOutline}
                    onClick={() => navigate("/etv")}
                >
                    Teruskan ke Tab ETV
                </button>
            ) : null}
            {isPaid && ["package", "admin"].includes(type) && (
                <p style={{ color: "#777", fontSize: 12, marginTop: 12 }}>Sistem Admin akan dibuka secara automatik...</p>
            )}
        </div>
    );
}
